/**
 * Cross-host URLs between the public site and the portals.
 *
 * Each audience lives on its own host with its own cookie, so a link that
 * stays on the wrong host looks like a working CTA and then dumps the user
 * on sign-in.
 */

function hostnameOf(explicit?: string): string {
  if (explicit !== undefined) return explicit;
  return typeof window === "undefined" ? "" : window.location.hostname;
}

/** True on UAT public and portal hosts. Production must not show UAT copy. */
export function isUatHost(hostname?: string): boolean {
  const host = hostnameOf(hostname);
  return host.startsWith("uat.") || host.includes("-uat.") || host.includes("-uat");
}

function asHash(path: string): string {
  if (path.startsWith("#")) return path;
  return `#${path.startsWith("/") ? path : `/${path}`}`;
}

/**
 * Marketing-site URL for a public hash path (`/legal/terms` → `#/legal/terms`).
 * Portal hosts resolve to the matching public environment.
 */
export function publicSiteUrl(hashPath = "/", hostname?: string): string {
  const host = hostnameOf(hostname);
  const hash = asHash(hashPath);
  if (host.startsWith("uat.") || host.includes("-uat.") || host.includes("-uat")) {
    return `https://uat.clipmaster.io/${hash}`;
  }
  if (host === "clipmaster.io" || host.endsWith(".clipmaster.io")) {
    return `https://clipmaster.io/${hash}`;
  }
  const portal = /^cm-portal-(?:creators|brands|admin)-(\w+)\.(.+)$/.exec(host);
  if (portal) return `https://cm-web-public-${portal[1]}.${portal[2]}/${hash}`;
  if (host.startsWith("cm-web-public-")) return `https://${host}/${hash}`;
  if (!host || host === "localhost" || host === "127.0.0.1") {
    return `https://uat.clipmaster.io/${hash}`;
  }
  return `/${hash}`;
}

/**
 * Portal URL for a public-site hand-off.
 */
export function portalUrl(
  audience: "creator" | "brand" | "staff",
  path = "/",
  hostname?: string,
): string {
  const host = hostnameOf(hostname);
  const sub = { creator: "creators", brand: "brands", staff: "admin" }[audience];
  if (host.startsWith("uat.")) return `https://${sub}-uat.clipmaster.io${path}`;
  if (host === "clipmaster.io" || host.endsWith(".clipmaster.io")) {
    return `https://${sub}.clipmaster.io${path}`;
  }
  const m = /^cm-web-public-(\w+)\.(.+)$/.exec(host);
  if (m) return `https://cm-portal-${sub}-${m[1]}.${m[2]}${path}`;
  if (!host || host === "localhost" || host === "127.0.0.1") {
    if (audience === "staff") return "https://admin-uat.clipmaster.io/";
    const intent = path.includes("forgot") ? "recover"
      : path.includes("login") ? "login" : "signup";
    return `#/${intent}?as=${audience}`;
  }
  return path;
}
